import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Film, Loader2 } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import logo from "@/assets/logo-white.png";
import { useNativelyEnvironment } from "@/hooks/useNativelyEnvironment";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = z
  .object({
    fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
    email: z.string().email("Invalid email address"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ProducerLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { isNativelyApp, isReady } = useNativelyEnvironment();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Login state. email pre-fills from ?email= so a producer arriving
  // from the mobile crew app's "Upgrade to Producer" banner doesn't
  // have to retype their address (the banner appends ?email=<user
  // .email>).
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // ?returnTo=/foo lets callers (UpgradePage's auth guard, anywhere
  // a deep-link bounces through login) come back where they started
  // after a successful sign-in. Allow only same-origin paths
  // (must start with "/", no protocol/host) so this can't be turned
  // into an open-redirect.
  const returnToRaw = searchParams.get("returnTo");
  const returnTo =
    returnToRaw && returnToRaw.startsWith("/") && !returnToRaw.startsWith("//")
      ? returnToRaw
      : null;
  const postLoginPath = returnTo ?? "/dashboard";

  // Signup state
  const [signupFullName, setSignupFullName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupRememberMe, setSignupRememberMe] = useState(false);

  // Redirect native app users to crew login
  useEffect(() => {
    if (isReady && isNativelyApp) {
      navigate("/crew-login", { replace: true });
    }
  }, [isReady, isNativelyApp, navigate]);

  useEffect(() => {
    // Show success toast if user just verified their email
    if (searchParams.get("verified") === "true") {
      toast({
        title: "Email Verified!",
        description: "Your email has been verified successfully. Please log in to continue.",
      });
    }
  }, [searchParams, toast]);

  // Email hint set by the mobile crew app's "Upgrade to Producer"
  // banner. When provided, an already-existing web session that
  // belongs to a DIFFERENT email gets signed out before the
  // auto-redirect — otherwise producer X already on the laptop would
  // silently land on /upgrade as crew Y. The compare lowercases +
  // trims both sides so casing/whitespace differences don't matter.
  const emailHint = (searchParams.get("email") ?? "").trim().toLowerCase();

  useEffect(() => {
    // Check if already logged in
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) return;
      const sessionEmail = (session.user.email ?? "").trim().toLowerCase();
      if (emailHint && sessionEmail && sessionEmail !== emailHint) {
        // Foreign session — sign out, then leave the user on the
        // login form pre-filled with the email they came from. We
        // intentionally do NOT navigate(postLoginPath) here; the
        // form is now ready for them to type their password.
        await supabase.auth.signOut();
        queryClient.clear();
        return;
      }
      queryClient.clear();
      navigate(postLoginPath);
    });

    // Listen for auth changes - but NOT during signup (handled separately)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only auto-redirect on login, not signup (signup redirects to /choose-trial manually)
      if (event === "SIGNED_IN" && session && !isSigningUp) {
        // Clear any stale pre-auth cached queries before navigating
        queryClient.clear();
        navigate(postLoginPath);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isSigningUp, postLoginPath, emailHint, queryClient]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validated = loginSchema.parse({ email, password });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Login failed",
            description: "Invalid email or password",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Login failed",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.session) {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in",
        });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsSigningUp(true); // Prevent onAuthStateChange from redirecting to dashboard

    try {
      const validated = signupSchema.parse({
        fullName: signupFullName,
        email: signupEmail,
        password: signupPassword,
        confirmPassword: signupConfirmPassword,
      });

      const { data, error } = await supabase.auth.signUp({
        email: validated.email,
        password: validated.password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            full_name: validated.fullName,
            signup_type: "producer",
          },
        },
      });

      if (error) {
        setIsSigningUp(false); // Reset so login still works
        if (error.message.includes("User already registered")) {
          toast({
            title: "Signup failed",
            description: "This email is already registered. Please login instead.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Signup failed",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.user) {
        // Verification email is sent automatically by DB trigger on profiles table
        toast({
          title: "Welcome to PROduce!",
          description: "Choose your trial plan to get started.",
        });

        // Navigate to trial selection page
        navigate("/choose-trial");
      }
    } catch (error) {
      setIsSigningUp(false); // Reset on error so login still works
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src={logo} alt="PROduce Logo" className="mx-auto mb-6 h-16" />
          <h1 className="mb-2 text-3xl font-bold">Producer Portal</h1>
          <p className="text-muted-foreground">
            Sign in or create an account to manage your productions
          </p>
        </div>

        {/* Login/Signup Tabs */}
        <Card className="border-2 bg-card/50 p-8 backdrop-blur-xl">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-primary">
            <Film className="h-10 w-10 text-white" />
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="mb-6 grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="producer@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <PasswordInput
                    id="login-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/auth/request-password-reset"
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <label
                    htmlFor="remember-me"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Remember me
                  </label>
                </div>

                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="producer@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <PasswordInput
                    id="signup-password"
                    placeholder="At least 6 characters"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                  <PasswordInput
                    id="signup-confirm-password"
                    placeholder="Confirm your password"
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="signup-remember-me"
                    checked={signupRememberMe}
                    onCheckedChange={(checked) => setSignupRememberMe(checked as boolean)}
                  />
                  <label
                    htmlFor="signup-remember-me"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Remember me
                  </label>
                </div>

                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Back to home
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ProducerLogin;
